import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import styled from 'styled-components';
import {Spinner} from "@chakra-ui/react";
import {Virtuoso} from 'react-virtuoso';
import {debounce} from "lodash/function";
import {useBaseCommunity, useIsBlocked, useLoggedIn, useStoreProfile} from "../store";
import {themecolors} from "../utils/themecolors";
import {useAdConfig} from "../api/ads";
import storage from "../utils/storage";
import {useActions} from "../app_context";
import isStandardBaseCommunity from "../utils/base_community";
import {thedonald} from "../store/reducer";
import CondensedPost from './condensed_post';
import CardPost from './card_post';
import ScrollToTop from "./scroll_to_top";
import PostActions from "./post_actions";
import RefreshFeed from "./refresh_feed";
import DynamicAd from "./ads/dynamic_ad";
import FeedContribute from "./feed_inserts/feed_contribute";

export const Item = styled(({className, item, expand, onOpenPost, first: _1, topics: _2, queueContext, sort, isCommunityFeed, index, feedKey, isTrending, community,
                            expandState, setExpandState, inMainFeed}) => {
    const Post = expand === 'card' ? CardPost : CondensedPost;
    if (item?.Component) {
        const {Component, prevAdTier = 2, nextAdTier = 2} = item;
        return <Component className={className} index={index} feedKey={feedKey} community={community}
                          prevAdTier={prevAdTier} nextAdTier={nextAdTier} expand={expand} />;
    }
    const handleClick = (event) => {
        event.stopPropagation();
        if (item) {
            onOpenPost(item);
        }
    };
    return (
        <div className={className} onClick={handleClick}>
            <Post post={item} mainFeedIndex={inMainFeed ? index : false} inFeed queueContext={queueContext} sort={sort} isCommunityFeed={isCommunityFeed} isTrending={isTrending} expandState={expandState} setExpandState={setExpandState}>
                <PostActions post={item} isTrending={isTrending}/>
            </Post>
        </div>
    );
})`
  
  ${({first, theme, noBorders}) => (!noBorders && !first) && `
    ${CardPost} {
        border-top: 1px solid ${theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.lighter_border};
    }
  `}


  ${({theme, noBorders}) => (!noBorders) && `
    ${CondensedPost} {
       border-bottom: 1px solid ${theme.color.name === 'dark' ? themecolors.dark_background_5 : themecolors.lighter_border};
    }
  `}

`;

const RefreshFeedContainer = styled.div`
  position: sticky;
  top: 40px;
  z-index: 2;
  height: 0;
  width: 100%;
`;

const SpinnerContainer = styled.div`
  text-align: center;
  padding: 48px 0 96px 0;
`;

const PostList = styled(({className, feedKey, expand, community, posts, loadNextPage, topics, loading, hideBlocked, sort, signalRef, polled, refresh, inFeed,
                         isCommunityFeed, showAds, restoredState, isTrending = false, inMainFeed}) => {

    const isStandardBase = isStandardBaseCommunity();

    const loggedIn = useLoggedIn();
    const adConfig = useAdConfig();
    const {theme} = useStoreProfile();
    const scrollToTop = React.useCallback(() => {
        document.scrollingElement.scrollTop = 0;
    }, []);
    React.useLayoutEffect(scrollToTop, [feedKey]);
    if (signalRef.current === 'scrollToTop') {
        signalRef.current = null;
        scrollToTop();
    }

    const history = useHistory();
    const openPost = React.useCallback((post) => {
        if (community) {
            history.push(`/c/${community}/p/${post.uuid}`);
        } else {
            history.push(`/p/${post.uuid}`);
        }
    }, [history, community]);
    const isBlocked = useIsBlocked();
    const baseCommunity = useBaseCommunity().toLowerCase();
    const { items, pinnedKeys } = React.useMemo(() => {
        const items = [];
        const pinnedKeys = [];
        for (let index = 0; index < posts.length; index++) {
            if (inFeed && inFeed[index]) {
                const key = `inFeed-${index}`;
                items.push({ uuid: key, Component: inFeed[index] });
                pinnedKeys.push(key);
            }
            if (index === 3 && loggedIn && baseCommunity !== "thedonald") {
                // support
                const key = `support-${index}`;
                items.push({ uuid: key, Component: FeedContribute});
                pinnedKeys.push(key);
            }
            if (showAds && adConfig && adConfig.v2) {
                const {guest: guestFreq, user: userFreq} = isStandardBase ? adConfig.v2.feedFrequency : adConfig.v2.feedFrequency.thirdParty;
                const {guest: guestInd, user: userInd} = isStandardBase ? adConfig.v2.feedStartingIndex : adConfig.v2.feedStartingIndex.thirdParty;
                if ((loggedIn && index >= userInd) || (!loggedIn && index >= guestInd)) {
                    const ind = loggedIn ? userInd : guestInd;
                    const freq = loggedIn ? userFreq : guestFreq;
                    if (index === ind || ((index - ind) % freq === 0)) {
                        const key = `inFeed-${index}`;

                        const prevAdTier = posts[index - 1]?.ad_tier ?? 2;
                        const nextAdTier = posts[index + 1]?.ad_tier ?? 2;
                        items.push({ uuid: key, Component: DynamicAd, prevAdTier,
                            nextAdTier});
                        pinnedKeys.push(key);
                    }
                }
            }
            if (!hideBlocked || !isBlocked(posts[index]?.author)) {
                items.push(posts[index]);
            }
        }
        return { items, pinnedKeys };
    }, [posts, hideBlocked, isBlocked]);

    const [dismissedRefresh, setDismissedRefresh] = useState(() => {
        const storedValue = localStorage.getItem('dismissRefreshFeed');
        return storedValue && parseInt(storedValue, 10) > Date.now() - 30 * 60 * 1000;
    });
    const handleDismissRefreshFeed = () => {
        storage.setItem("dismissRefreshFeed", Date.now());
        setDismissedRefresh(true);
    };

    const actions = useActions();

    const virtuosoRef = useRef(null); // create a ref to store the Virtuoso instance

    const [expandState, setExpandState] = useState(restoredState?.expandState ? restoredState.expandState : []);

    useEffect(() => {
        actions.feedStateChanged(feedKey, {expandState});
    }, [expandState]);

    // callback function to receive the state
    const getStateCallback = (state) => {
        if (state.scrollTop > 0) {
            actions.feedStateChanged(feedKey, {state, updatedTime: Date.now()});
        } else {
            actions.feedStateChanged(feedKey, {state: false, updatedTime: Date.now()});
        }
    }

    const handleGetState = () => {
        if (virtuosoRef.current) {
            virtuosoRef.current.getState(getStateCallback); // access the getState() method from the Virtuoso instance
        }
    };

    useEffect(() => {
        if (!restoredState) {
            handleGetState();
        }
    }, []);

    useEffect(() => {
        // function to update visibleItemIndex based on scrollTop
        const updateVisibleItemIndex = () => {
            handleGetState();
        };

        // debounce the update function with 50ms delay
        const debouncedUpdate = debounce(updateVisibleItemIndex, 50);

        // event listener for scroll
        const handleScroll = () => {
            debouncedUpdate();
        };

        // attach event listener when component mounts
        window.addEventListener('scroll', handleScroll);

        // cleanup event listener when component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll);
            // cancel any pending debounced function calls
            debouncedUpdate.cancelled = true;
        };
    }, [feedKey]);

    const restoreData = restoredState && restoredState.state ? {restoreStateFrom: restoredState.state} : {};

    return (<>
        {polled && !dismissedRefresh && <RefreshFeedContainer><RefreshFeed onClick={() => refresh(feedKey)} close={() => handleDismissRefreshFeed()}/></RefreshFeedContainer>}
            <Virtuoso
                ref={virtuosoRef}
                style={{marginBottom: 56}}
                totalCount={items.length}
                useWindowScroll
                endReached={loadNextPage}
                data={items}
                overscan={1500}
                {...restoreData}
                itemContent={index => {

                    return items[index] ? (
                        <Item
                            index={index}
                            feedKey={feedKey}
                            key={index}
                            item={items[index]}
                            sort={sort}
                            expand={expand}
                            onOpenPost={openPost}
                            topics={topics}
                            first={index === 0}
                            isCommunityFeed={isCommunityFeed}
                            isTrending={isTrending}
                            community={community}
                            expandState={expandState}
                            setExpandState={setExpandState}
                            inMainFeed={inMainFeed}
                        />
                    ) : <div />;
            }}
        />
        {loading && (
                <SpinnerContainer>
                    <Spinner
                        thickness='4px'
                        speed='0.65s'
                        emptyColor={theme === "dark" ? themecolors.dark_background_5 : themecolors.light_low_impact_text}
                        color={theme === "dark" ? themecolors.dark_background_2 : themecolors.pure_white}
                        size='lg'
                    />
                </SpinnerContainer>
            )
        }
        <ScrollToTop onClick={scrollToTop}/>
    </>);
})`
`;

export default PostList;
